/**
 * ExpandableCards — list rendering primitive used across the app instead of
 * a classic table. Each row renders as a rounded card with an always-visible
 * header and a details section that toggles open on click.
 *
 * Props:
 *   items       array of records
 *   getKey      (item) => string  (stable React key)
 *   renderHeader(item, { open })  JSX for the summary row
 *   renderDetails(item)           JSX for the expanded body
 *   emptyText   fallback string when items is empty
 *   defaultOpen optional (item) => boolean — cards whose predicate is true
 *               start expanded. If omitted, all start collapsed.
 *
 * `<CardField label value />` is a small helper for consistent label/value
 * pairs inside renderDetails.
 */
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'

export function ExpandableCards({
  items,
  getKey,
  renderHeader,
  renderDetails,
  emptyText = 'Sin datos',
  defaultOpen,
  grid = false,
}) {
  const initial = useMemo(() => {
    if (!defaultOpen) return {}
    const acc = {}
    for (const it of items || []) {
      const k = getKey(it)
      if (defaultOpen(it)) acc[k] = true
    }
    return acc
  }, [items, getKey, defaultOpen])

  const [openMap, setOpenMap] = useState(initial)

  if (!items || items.length === 0) {
    return (
      <div className="p-12 text-center text-expo-muted bg-white rounded-2xl border border-expo-line">
        {emptyText}
      </div>
    )
  }

  const toggle = (k) => setOpenMap((m) => ({ ...m, [k]: !m[k] }))

  return (
    <div className={grid ? 'grid grid-cols-1 xl:grid-cols-2 gap-3 items-start' : 'space-y-3'}>
      {items.map((item) => {
        const key = getKey(item)
        const open = Boolean(openMap[key])
        return (
          <div
            key={key}
            className={cn(
              'bg-white rounded-2xl border border-expo-line shadow-card overflow-hidden transition-shadow hover:shadow-card-lg',
              grid && open && 'xl:col-span-2',
            )}
          >
            <button
              type="button"
              onClick={() => toggle(key)}
              className={cn(
                'w-full px-5 py-4 flex items-center justify-between gap-4 text-left',
                'hover:bg-expo-light/60 transition-colors',
              )}
            >
              <div className="min-w-0 flex-1">{renderHeader(item, { open })}</div>
              <div className="flex-shrink-0" style={{ color: '#1E6E3A' }}>
                {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              </div>
            </button>
            {open && (
              <div className="px-5 pb-5 pt-1 border-t border-expo-line bg-expo-light/30">
                {renderDetails(item)}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function CardField({ label, value, className }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="uppercase tracking-wide" style={{ fontSize: '11px', fontWeight: 600, color: '#8B9B8F' }}>
        {label}
      </div>
      <div className="text-sm break-words" style={{ fontWeight: 600, color: '#26352C' }}>
        {value ?? '—'}
      </div>
    </div>
  )
}

export function CardGrid({ children, cols = 3 }) {
  const gridCls = cols === 4
    ? 'grid grid-cols-2 md:grid-cols-4 gap-4'
    : cols === 2
      ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
      : 'grid grid-cols-2 md:grid-cols-3 gap-4'
  return <div className={gridCls}>{children}</div>
}
